import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import stripesFinalForm from '@folio/stripes/final-form';
import {
  Select,
  Datepicker,
  Timepicker,
  Accordion,
  AccordionSet,
  Row,
  Col,
  TextArea,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { required } from '@folio/stripes/util';
import { TemplateEditor } from '@folio/stripes-template-editor';

import CronBuilder from './CronBuilder';
import OutputFormatSelector from './OutputFormatSelector';
import RecipientsList from './RecipientsList';
import LocalizedTextField from '../common/LocalizedTextField';
import scheduleTokens, { SCHEDULE_CATEGORY } from './scheduleTokens';
import ScheduleTokensList from './ScheduleTokensList';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validates recipients array:
// - at least one recipient
// - every recipient has a valid email
const validateRecipients = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return <FormattedMessage id="ui-reports.recipients.empty" defaultMessage="Add at least one recipient" />;
  }
  const hasInvalid = value.some(r => !r.email || !EMAIL_REGEX.test(r.email));
  if (hasInvalid) {
    return <FormattedMessage id="ui-reports.recipients.invalidEmail" defaultMessage="All recipients must have a valid email address" />;
  }
  return undefined;
};

const validateFutureDate = (value, isEdit = false) => {
  if (!value || isEdit) return undefined;
  const date = new Date(value);
  if (isNaN(date.getTime())) return undefined;
  // Compare against start of today — allow selecting today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date < today) {
    return (
      <FormattedMessage id="ui-reports.scheduledReports.startDate.pastError" />
    );
  }
  return undefined;
};

const validateEndDate = (endDate, allValues) => {
  if (!endDate || !allValues.startDate) return undefined;
  // endDate must be same day or after startDate
  if (endDate < allValues.startDate) {
    return (
      <FormattedMessage
        id="ui-reports.scheduledReports.endDate.beforeStartError"
        defaultMessage="End date must be after start date"
      />
    );
  }
  return undefined;
};

function ScheduleForm({ handleSubmit, form, initialValues, isEdit, formRef }) {
  // نمرر form instance للـ modal عبر formRef
  React.useEffect(() => {
    if (!formRef) return;
    if (typeof formRef === 'function') {
      formRef(form);
    } else {
      formRef.current = form;
    }
  }, [form, formRef]);

  const intl = useIntl();

  const [frequency, setFrequency] = useState(
    initialValues?.frequency || 'daily',
  );
  const [showCronBuilder, setShowCronBuilder] = useState(
    (initialValues?.frequency || 'daily') === 'custom',
  );

  React.useEffect(() => {
    if (initialValues?.frequency) {
      setFrequency(initialValues.frequency);
      setShowCronBuilder(initialValues.frequency === 'custom');
    }
  }, [initialValues?.frequency]);

  const dayOfWeekOptions = [
    {
      value: 'MON',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.MON' }),
    },
    {
      value: 'TUE',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.TUE' }),
    },
    {
      value: 'WED',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.WED' }),
    },
    {
      value: 'THU',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.THU' }),
    },
    {
      value: 'FRI',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.FRI' }),
    },
    {
      value: 'SAT',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.SAT' }),
    },
    {
      value: 'SUN',
      label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.SUN' }),
    },
  ];

  const dayOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

  const frequencyOptions = [
    {
      value: 'once',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.once',
      }),
    },
    {
      value: 'hourly',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.hourly',
      }),
    },
    {
      value: 'daily',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.daily',
      }),
    },
    {
      value: 'weekly',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.weekly',
      }),
    },
    {
      value: 'monthly',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.monthly',
      }),
    },
    {
      value: 'yearly',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.yearly',
      }),
    },
    {
      value: 'custom',
      label: intl.formatMessage({
        id: 'ui-reports.scheduledReports.frequency.custom',
      }),
    },
  ];

  const monthOfYearOptions = [
    { value: '1',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.january',  defaultMessage: 'January'   }) },
    { value: '2',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.february', defaultMessage: 'February'  }) },
    { value: '3',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.march',    defaultMessage: 'March'     }) },
    { value: '4',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.april',   defaultMessage: 'April'     }) },
    { value: '5',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.may',     defaultMessage: 'May'       }) },
    { value: '6',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.june',    defaultMessage: 'June'      }) },
    { value: '7',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.july',    defaultMessage: 'July'      }) },
    { value: '8',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.august',  defaultMessage: 'August'    }) },
    { value: '9',  label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.september', defaultMessage: 'September' }) },
    { value: '10', label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.october',  defaultMessage: 'October'   }) },
    { value: '11', label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.november', defaultMessage: 'November'  }) },
    { value: '12', label: intl.formatMessage({ id: 'ui-reports.scheduledReports.month.december', defaultMessage: 'December'  }) },
  ];

  const handleFrequencyChange = (e) => {
    const value = e.target.value;
    setFrequency(value);
    setShowCronBuilder(value === 'custom');
    form.change('frequency', value);
  };

  return (
    <form id="schedule-report-form" onSubmit={handleSubmit}>
      <AccordionSet>
        {/* ── Basic Info ── */}
        <Accordion
          label={
            <FormattedMessage
              id="ui-reports.scheduledReports.basicInfo"
              defaultMessage="Basic Information"
            />
          }
          id="basic-info"
        >
          <Row>
            <Col xs={12}>
              <FieldArray name="name">
                {({ fields }) => (
                  <LocalizedTextField
                    fields={fields}
                    fieldLabel={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.name"
                        defaultMessage="Schedule Name"
                      />
                    }
                    isRequired
                  />
                )}
              </FieldArray>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FieldArray name="description">
                {({ fields }) => (
                  <LocalizedTextField
                    fields={fields}
                    fieldLabel={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.description"
                        defaultMessage="Description"
                      />
                    }
                    fieldComponent={TextArea}
                  />
                )}
              </FieldArray>
            </Col>
          </Row>
        </Accordion>

        {/* ── Schedule ── */}
        <Accordion
          label={<FormattedMessage id="ui-reports.scheduledReports.schedule" />}
          id="schedule"
        >
          {/* Row 1: Frequency + Start Date + Execution Time */}
          <Row>
            <Col xs={6}>
              <Field
                name="frequency"
                component={Select}
                label={
                  <FormattedMessage id="ui-reports.scheduledReports.frequency" />
                }
                dataOptions={frequencyOptions}
                required
                validate={required}
                onChange={handleFrequencyChange}
              />
            </Col>
            <Col xs={3}>
              <Field
                name="startDate"
                component={Datepicker}
                label={
                  <FormattedMessage id="ui-reports.scheduledReports.startDate" />
                }
                required
                validate={(v) => required(v) || validateFutureDate(v, isEdit)}
                backendDateStandard="YYYY-MM-DD"
              />
            </Col>
            {frequency !== 'custom' && (
              <Col xs={3}>
                {frequency === 'hourly' ? (
                  <Field
                    name="executionMinute"
                    component={Select}
                    label={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.executionMinute"
                        defaultMessage="Minute of each hour"
                      />
                    }
                    dataOptions={Array.from({ length: 12 }, (_, i) => ({
                      value: String(i * 5),
                      label: `:${String(i * 5).padStart(2, '0')}`,
                    }))}
                    required
                    validate={required}
                  />
                ) : (
                  <Field
                    name="executionTime"
                    component={Timepicker}
                    label={
                      <FormattedMessage id="ui-reports.scheduledReports.executionTime" />
                    }
                    required
                    validate={required}
                    timeZone="UTC"
                    locale="en"
                  />
                )}
              </Col>
            )}
          </Row>

          {/* Row 2: Day selectors (weekly / monthly / yearly) */}
          {(frequency === 'weekly' || frequency === 'monthly' || frequency === 'yearly') && (
            <Row>
              {frequency === 'weekly' && (
                <Col xs={6}>
                  <Field
                    name="dayOfWeek"
                    component={Select}
                    label={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.dayOfWeek"
                        defaultMessage="Day of Week"
                      />
                    }
                    dataOptions={dayOfWeekOptions}
                    required
                    validate={required}
                  />
                </Col>
              )}
              {(frequency === 'monthly' || frequency === 'yearly') && (
                <Col xs={frequency === 'yearly' ? 3 : 6}>
                  <Field
                    name="dayOfMonth"
                    component={Select}
                    label={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.dayOfMonth"
                        defaultMessage="Day of Month"
                      />
                    }
                    dataOptions={dayOfMonthOptions}
                    required
                    validate={required}
                  />
                </Col>
              )}
              {frequency === 'yearly' && (
                <Col xs={3}>
                  <Field
                    name="monthOfYear"
                    component={Select}
                    label={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.monthOfYear"
                        defaultMessage="Month of Year"
                      />
                    }
                    dataOptions={monthOfYearOptions}
                    required
                    validate={required}
                  />
                </Col>
              )}
            </Row>
          )}

          {/* Row 3: End Date + End Time */}
          <Row>
            <Col xs={6}>
              <Field
                name="endDate"
                component={Datepicker}
                label={
                  <FormattedMessage id="ui-reports.scheduledReports.endDate" />
                }
                backendDateStandard="YYYY-MM-DD"
                validate={(v, allValues) => validateEndDate(v, allValues)}
              />
            </Col>
            <Col xs={6}>
              <Field
                name="endTime"
                component={Timepicker}
                label={
                  <FormattedMessage
                    id="ui-reports.scheduledReports.endTime"
                    defaultMessage="End Time"
                  />
                }
                timeZone="UTC"
                locale="en"
              />
            </Col>
          </Row>

          {showCronBuilder && (
            <Row>
              <Col xs={12}>
                <Field
                  name="cronExpression"
                  component={CronBuilder}
                  label={
                    <FormattedMessage id="ui-reports.scheduledReports.cronExpression" />
                  }
                  required
                  validate={required}
                />
              </Col>
            </Row>
          )}
        </Accordion>

        {/* ── Output ── */}
        <Accordion
          label={
            <FormattedMessage
              id="ui-reports.scheduledReports.output"
              defaultMessage="Output Settings"
            />
          }
          id="output"
        >
          <Row>
            <Col xs={12}>
              <Field
                name="outputFormat"
                component={OutputFormatSelector}
                label={
                  <FormattedMessage
                    id="ui-reports.scheduledReports.outputFormat"
                    defaultMessage="Output Format"
                  />
                }
                required
                validate={required}
              />
            </Col>
          </Row>
        </Accordion>

        {/* ── Email ── */}
        <Accordion
          label={
            <FormattedMessage
              id="ui-reports.scheduledReports.email"
              defaultMessage="Email Settings"
            />
          }
          id="email"
        >
          <Row>
            <Col xs={12}>
              <Field
                name="recipients"
                component={RecipientsList}
                label={
                  <FormattedMessage
                    id="ui-reports.scheduledReports.recipients"
                    defaultMessage="Recipients"
                  />
                }
                required
                validate={validateRecipients}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FieldArray name="emailSubject">
                {({ fields }) => (
                  <LocalizedTextField
                    fields={fields}
                    fieldLabel={
                      <FormattedMessage
                        id="ui-reports.scheduledReports.emailSubject"
                        defaultMessage="Email Subject"
                      />
                    }
                    isRequired
                  />
                )}
              </FieldArray>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FieldArray
                name="emailBody"
                component={LocalizedTextField}
                fieldLabel={
                  <FormattedMessage
                    id="ui-reports.scheduledReports.emailBody"
                    defaultMessage="Email Body"
                  />
                }
                fieldComponent={TemplateEditor}
                rowStyle={{ marginBottom: '1.5rem' }}
                fieldComponentProps={{
                  tokens: scheduleTokens,
                  tokensList: ScheduleTokensList,
                  previewModalHeader: (
                    <FormattedMessage
                      id="ui-reports.scheduledReports.emailBody.preview"
                      defaultMessage="Email Body Preview"
                    />
                  ),
                  selectedCategory: SCHEDULE_CATEGORY,
                }}
              />
            </Col>
          </Row>
        </Accordion>
      </AccordionSet>
    </form>
  );
}

ScheduleForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
  formRef: PropTypes.object,
};

export default stripesFinalForm({
  navigationCheck: true,
  subscription: { values: true, pristine: true },
  enableReinitialize: true,
  initialValuesEqual: (a, b) => JSON.stringify(a?.name) === JSON.stringify(b?.name)
    && JSON.stringify(a?.emailSubject) === JSON.stringify(b?.emailSubject),
  mutators: { ...arrayMutators },
})(ScheduleForm);
